import {ChangeDetectorRef, Component, ElementRef, Input, OnInit, ViewContainerRef} from '@angular/core';
import {DataResult} from '@progress/kendo-data-query';
import {RowClassArgs} from '@progress/kendo-angular-grid';
import {DialogCloseResult, DialogService} from '@progress/kendo-angular-dialog';
import {ConfigurationService} from '../../configuration.service';
import {NotificationPopupService} from '../../../shared/notification-popup/services/notification-popup.service';
import {AuthService} from '../../../auth/auth.service';
import {
    DayLabel,
    MarketBlock,
    MarketGroup,
    MarketsConfigResponse,
    MarketsLastChanged,
    MarketsSavePayload,
    MarketsUnassigned
} from '../../shared/models/markets-config.model';
import {BaseMarketEditor} from '../base-market-editor';
import {blocksOverlap, toRangesCsv} from '../shared/markets-csv.util';
import {ImportMarketsDialogComponent} from '../import-markets-dialog/import-markets-dialog.component';
import {PriorityOrderDialogComponent, PriorityOrderRow} from '../priority-order-dialog/priority-order-dialog.component';

const ALL_DAYS: DayLabel[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

interface EditableBlock {
    // Client-only, used for trackBy/edit-targeting - never sent to the server.
    key: string;
    // The market_assignment_rules row id, or null for a block created this session.
    id: number | null;
    rsidFrom: string;
    rsidTo: string;
    days: DayLabel[] | null;
    dateFrom: string | null;
    dateTo: string | null;
    // Never shown or edited - round-tripped from load straight through to save.
    trainOrigin: string | null;
    trainDestination: string | null;
    // undefined once this block has been added/edited this session and not yet saved - the
    // server hasn't recomputed it yet, so showing the old number would be showing stale data.
    covered?: number;
    // Load-time sort hint only, used once by rebuildPriorityOrder() to seed priorityOrder -
    // never read again after that; priorityOrder's own array position is authoritative from
    // then on. A block added this session (addBlock) has none - it's pushed straight onto
    // priorityOrder instead.
    rank?: number;
}

interface EditableMarket {
    key: string;
    name: string;
    // True for a market created this session, not yet saved - drives whether deleting it or
    // renaming it needs to log anything (nothing was ever persisted under it).
    isNew: boolean;
    blocks: EditableBlock[];
}

interface MarketRow {
    isGroupRow: boolean;
    // Pinned rows are never toggled and never gain block rows - a distinct flag rather than
    // overloading isGroupRow, since the two render completely differently.
    isPinnedRow?: boolean;
    // A block row currently open for add/edit renders as an in-place panel instead of its
    // normal cells.
    isEditRow?: boolean;
    market: string;
    marketRef?: EditableMarket;
    blockRef?: EditableBlock;
    // Group rows only:
    itemCount?: number;
    coveredSum?: number;
    // Data rows only:
    service?: string;
    days?: DayLabel[] | null;
    dateFrom?: string | null;
    dateTo?: string | null;
    covered?: number;
    // How many other blocks (any market) this one overlaps in RSID range/days/dates -
    // informational only, non-blocking: real data (EMR) relies on rank/priority order to
    // resolve exactly this. 0 when it overlaps nothing.
    overlapCount?: number;
}

// Edit-panel working state, bound via ngModel while a block is being added or edited. Kept
// separate from EditableBlock so Cancel can throw the draft away without having mutated
// anything on the block it was opened from.
interface BlockDraft {
    rsidFrom: string;
    rsidTo: string;
    days: Set<DayLabel>;
    dateRangeEnabled: boolean;
    dateFrom: string | null;
    dateTo: string | null;
}

@Component({
    selector: 'app-ranges-markets',
    templateUrl: './ranges-markets.component.html',
    styleUrls: ['./ranges-markets.component.scss']
})
export class RangesMarketsComponent extends BaseMarketEditor implements OnInit {
    @Input() initial!: MarketsConfigResponse;

    public gridData: DataResult = {data: [], total: 0};
    public marketCount = 0;
    public serviceCount = 0;
    public lastChanged: MarketsLastChanged | null = null;
    public unassigned: MarketsUnassigned | null = null;

    // Only one block is ever open for add/edit at a time, matching the design reference -
    // opening a second one implicitly means the previous one's edits are still there
    // (nothing is thrown away, they just aren't visible until re-opened or saved).
    public draft: BlockDraft | null = null;
    public draftError: string | null = null;
    public readonly allDays = ALL_DAYS;

    public rowClass = (context: RowClassArgs): {[key: string]: boolean} => {
        const row = context.dataItem as MarketRow;
        return {
            'market-group-row': row.isGroupRow,
            'market-pinned-row': !!row.isPinnedRow,
            'market-edit-row': !!row.isEditRow
        };
    };

    private markets: EditableMarket[] = [];

    // The single source of truth for the global cross-market priority order - the exact same
    // EditableBlock object references as markets[].blocks, just in a different order. Grouping
    // by market (as `markets` does, for the per-market grid) can't represent an order that
    // interleaves across markets, which real data (EMR) does - so this flat order is what
    // drives rank on save/export, and what the Priority Order dialog reorders directly.
    private priorityOrder: EditableBlock[] = [];

    constructor(dialogService: DialogService,
                viewContainerRef: ViewContainerRef,
                elementRef: ElementRef,
                cdr: ChangeDetectorRef,
                authService: AuthService,
                notificationPopupSvc: NotificationPopupService,
                private configurationSvc: ConfigurationService) {
        super(dialogService, viewContainerRef, elementRef, cdr, authService, notificationPopupSvc);
    }

    // View-only for now: this reads rail.market_assignment_rules and renders it grouped by
    // market. The initial fetch/load-error state lives in the parent (markets.component.ts) -
    // by the time this component exists, `initial` is already a successful response.
    ngOnInit(): void {
        this.applyResponse(this.initial);
    }

    // A freshly added/edited block's covered count is stale (the server hasn't recomputed it
    // yet) rather than wrong, so it's shown as unknown rather than as a number that might not
    // be true anymore - never show a number that could be wrong.
    public coveredDisplay(row: MarketRow): number | string {
        if (row.isPinnedRow) {
            return row.covered ?? 0;
        }
        if (row.isGroupRow) {
            return row.coveredSum ?? 0;
        }
        return row.covered === undefined ? '—' : row.covered;
    }

    public coveredIsZero(row: MarketRow): boolean {
        if (row.isPinnedRow) {
            return false;
        }
        const value = row.isGroupRow ? row.coveredSum : row.covered;
        return !value && value !== undefined;
    }

    public coveredIsUnknown(row: MarketRow): boolean {
        return !row.isPinnedRow && !row.isGroupRow && row.covered === undefined;
    }

    // ---- Loading -------------------------------------------------------------------------

    private applyResponse(response: MarketsConfigResponse): void {
        this.lastChanged = response.lastChanged;
        this.unassigned = response.unassigned;
        this.markets = response.markets.map(market => this.toEditableMarket(market));
        this.rebuildPriorityOrder();

        this.expandedMarkets = new Set(response.markets.map(market => market.name));
        this.resetPendingChanges();
        this.editingKey = null;
        this.draft = null;
        this.draftError = null;
        this.marketCount = response.markets.length;
        this.serviceCount = response.markets.reduce((total, market) => total + (market.blocks?.length ?? 0), 0);
        this.rebuildRows();
    }

    // Flattens every block across every market and sorts by its load-time rank hint - the one
    // place priorityOrder is (re)seeded from scratch, called once after applyResponse and once
    // after applyImport. Untouched afterward: priorityOrder's own array order is authoritative
    // from this point on, mutated directly by addBlock/deleteBlock/deleteMarket/the Priority
    // Order dialog rather than re-derived from rank again.
    private rebuildPriorityOrder(): void {
        this.priorityOrder = this.markets
            .reduce((blocks, market) => blocks.concat(market.blocks), [] as EditableBlock[])
            .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0));
    }

    private toEditableMarket(market: MarketGroup): EditableMarket {
        return {
            key: this.newKey(),
            name: market.name,
            isNew: false,
            blocks: (market.blocks ?? []).map(block => this.toEditableBlock(block))
        };
    }

    private toEditableBlock(block: MarketBlock): EditableBlock {
        const [rsidFrom, rsidTo] = this.splitService(block.service);
        return {
            key: this.newKey(),
            id: block.id,
            rsidFrom,
            rsidTo,
            days: block.days,
            dateFrom: block.dateFrom,
            dateTo: block.dateTo,
            trainOrigin: block.trainOrigin,
            trainDestination: block.trainDestination,
            covered: block.covered,
            rank: block.rank
        };
    }

    // Matches the backend's own _parse_service_range: split on the first dash, a bare RSID
    // becomes a single-value range.
    private splitService(service: string): [string, string] {
        const dashIndex = service.indexOf('-');
        if (dashIndex === -1) {
            return [service, service];
        }
        return [service.slice(0, dashIndex), service.slice(dashIndex + 1)];
    }

    // ---- Toggling ------------------------------------------------------------------------

    public toggleMarket(market: string): void {
        if (this.expandedMarkets.has(market)) {
            this.expandedMarkets.delete(market);
        } else {
            this.expandedMarkets.add(market);
        }
        this.rebuildRows();
        this.cdr.detectChanges();
    }

    // ---- Market-level actions --------------------------------------------------------------

    public addMarket(): void {
        this.promptForMarketName('Add market', 'Add', '', this.markets.map(m => m.name)).subscribe(newName => {
            if (!newName) {
                return;
            }
            const market: EditableMarket = {key: this.newKey(), name: newName, isNew: true, blocks: []};
            this.markets.push(market);
            this.expandedMarkets.add(market.name);
            this.pushMarketCreateChange(market);
            this.marketCount = this.markets.length;
            this.addBlock(market);
        });
    }

    public renameMarket(market: EditableMarket): void {
        this.promptForMarketName('Rename market', 'Rename', market.name, this.markets.filter(m => m !== market).map(m => m.name))
            .subscribe(newName => {
                if (!newName) {
                    return;
                }
                this.renameMarketChange(market, newName);
                this.rebuildRows();
                this.cdr.detectChanges();
            });
    }

    public deleteMarket(market: EditableMarket): void {
        const message = market.blocks.length
            ? `Delete market "${market.name}" and all ${market.blocks.length} of its blocks?`
            : `Delete market "${market.name}"?`;
        this.confirm(message).subscribe(confirmed => {
            if (!confirmed) {
                return;
            }
            if (this.editingKey && market.blocks.some(block => block.key === this.editingKey)) {
                this.editingKey = null;
                this.draft = null;
            }

            this.deleteMarketChange(market, market.blocks.map(block => this.blockSnapshot(block)));
            this.markets = this.markets.filter(m => m !== market);
            this.priorityOrder = this.priorityOrder.filter(block => !market.blocks.includes(block));
            this.marketCount = this.markets.length;
            this.rebuildRows();
            this.cdr.detectChanges();
        });
    }

    // ---- Block-level actions ---------------------------------------------------------------

    public addBlock(market: EditableMarket): void {
        const block: EditableBlock = {
            key: this.newKey(), id: null, rsidFrom: '', rsidTo: '', days: null,
            dateFrom: null, dateTo: null, trainOrigin: null, trainDestination: null
        };
        market.blocks.push(block);
        // Lowest priority by default - a new rule never silently overrides an existing one's
        // precedence; the user promotes it explicitly via the Priority Order dialog if needed.
        this.priorityOrder.push(block);
        this.expandedMarkets.add(market.name);
        this.startEdit(block);
    }

    public editBlock(block: EditableBlock): void {
        this.startEdit(block);
    }

    public deleteBlock(market: EditableMarket, block: EditableBlock): void {
        this.confirm(`Delete "${this.formatService(block)}" from "${market.name}"?`).subscribe(confirmed => {
            if (!confirmed) {
                return;
            }
            if (this.editingKey === block.key) {
                this.editingKey = null;
                this.draft = null;
            }
            market.blocks = market.blocks.filter(b => b !== block);
            this.priorityOrder = this.priorityOrder.filter(b => b !== block);

            const pendingAdd = this.pendingChanges.find(c => c.itemKey === block.key && c.action === 'block_add');
            if (pendingAdd) {
                // Never saved in the first place - nothing to log.
                this.pendingChanges = this.pendingChanges.filter(c => c !== pendingAdd);
            } else {
                // Drop any not-yet-saved edit to this block too - block_delete's own
                // "before" snapshot already captures whatever state it was last saved in.
                this.pendingChanges = this.pendingChanges.filter(c => c.itemKey !== block.key);
                this.pendingChanges.push({
                    action: 'block_delete', market: market.name, before: this.blockSnapshot(block), after: null, itemKey: block.key
                });
            }

            this.rebuildRows();
            this.cdr.detectChanges();
        });
    }

    private startEdit(block: EditableBlock): void {
        this.editingKey = block.key;
        this.draftError = null;
        this.draft = {
            rsidFrom: block.rsidFrom,
            rsidTo: block.rsidTo,
            days: new Set(block.days ?? []),
            dateRangeEnabled: !!block.dateFrom,
            dateFrom: block.dateFrom,
            dateTo: block.dateTo
        };
        this.rebuildRows();
        this.cdr.detectChanges();
        this.scrollToEditingRow();
    }

    public toggleDraftDay(day: DayLabel): void {
        if (!this.draft) {
            return;
        }
        if (this.draft.days.has(day)) {
            this.draft.days.delete(day);
        } else {
            this.draft.days.add(day);
        }
    }

    public cancelEdit(market: EditableMarket, block: EditableBlock): void {
        // A just-added block that was never committed leaves nothing worth keeping around.
        if (!block.rsidFrom && !block.id) {
            market.blocks = market.blocks.filter(b => b !== block);
        }
        this.editingKey = null;
        this.draft = null;
        this.draftError = null;
        this.rebuildRows();
        this.cdr.detectChanges();
    }

    public commitEdit(market: EditableMarket, block: EditableBlock): void {
        const draft = this.draft;
        if (!draft) {
            return;
        }

        const rsidFrom = draft.rsidFrom.trim();
        const rsidTo = draft.rsidTo.trim();
        if (!rsidFrom || !rsidTo) {
            this.draftError = 'Enter a service or range.';
            return;
        }
        if (rsidFrom.length !== rsidTo.length) {
            this.draftError = 'From and to must be the same length.';
            return;
        }
        if (rsidFrom > rsidTo) {
            this.draftError = 'From/to are reversed.';
            return;
        }
        if (draft.dateRangeEnabled && (!draft.dateFrom || !draft.dateTo)) {
            this.draftError = 'Enter both a from and a to date, or turn the date range off.';
            return;
        }
        if (draft.dateRangeEnabled && draft.dateFrom > draft.dateTo) {
            this.draftError = 'Date range is reversed.';
            return;
        }

        // The concrete 7-day set, not null, when nothing's picked - same value the save
        // endpoint would normalise an empty selection to regardless, so what's staged here,
        // what's audited, and what actually lands in the DB all agree with each other.
        const days: DayLabel[] = draft.days.size ? this.allDays.filter(day => draft.days.has(day)) : [...this.allDays];
        const dateFrom = draft.dateRangeEnabled ? draft.dateFrom : null;
        const dateTo = draft.dateRangeEnabled ? draft.dateTo : null;
        // Overlaps are allowed - real data (EMR) relies on rank/priority order (see the
        // Priority Order dialog) to resolve a narrower rule overriding a broader one it
        // deliberately overlaps. The grid still flags overlaps via overlapCount, non-blocking.
        const before = this.blockSnapshot(block);
        Object.assign(block, {rsidFrom, rsidTo, days, dateFrom, dateTo, covered: undefined});
        const after = this.blockSnapshot(block);

        const pendingAdd = this.pendingChanges.find(c => c.itemKey === block.key && c.action === 'block_add');
        if (pendingAdd) {
            // Re-editing a block that was added this session, before it's ever been saved -
            // update the still-pending add rather than logging a separate edit on top of it.
            pendingAdd.after = after;
            pendingAdd.market = market.name;
        } else if (!block.id) {
            this.pendingChanges.push({action: 'block_add', market: market.name, before: null, after, itemKey: block.key});
        } else if (JSON.stringify(before) !== JSON.stringify(after)) {
            this.pendingChanges.push({action: 'block_edit', market: market.name, before, after, itemKey: block.key});
        }

        this.editingKey = null;
        this.draft = null;
        this.draftError = null;
        this.rebuildRows();
        this.cdr.detectChanges();
    }

    private blockSnapshot(block: EditableBlock): {service: string; days: DayLabel[] | null; dateFrom: string | null; dateTo: string | null} {
        return {service: this.formatService(block), days: block.days, dateFrom: block.dateFrom, dateTo: block.dateTo};
    }

    private formatService(block: EditableBlock): string {
        if (!block.rsidFrom) {
            return '';
        }
        return block.rsidFrom === block.rsidTo ? block.rsidFrom : `${block.rsidFrom}-${block.rsidTo}`;
    }

    // Non-blocking, informational only - overlaps are allowed (real data relies on rank/
    // priority order, see the Priority Order dialog, to resolve them). Computed once per
    // rebuildRows() call rather than per template evaluation.
    private computeOverlapCounts(): Map<string, number> {
        const counts = new Map<string, number>();
        const blocks = this.priorityOrder.filter(block => block.rsidFrom && block.rsidTo);
        for (let i = 0; i < blocks.length; i++) {
            for (let j = i + 1; j < blocks.length; j++) {
                if (blocksOverlap(blocks[i], blocks[j])) {
                    counts.set(blocks[i].key, (counts.get(blocks[i].key) ?? 0) + 1);
                    counts.set(blocks[j].key, (counts.get(blocks[j].key) ?? 0) + 1);
                }
            }
        }
        return counts;
    }

    // ---- Import / Export -------------------------------------------------------------------

    public importMarkets(): void {
        const dialogRef = this.dialogService.open({
            title: 'Import markets',
            content: ImportMarketsDialogComponent,
            appendTo: this.viewContainerRef,
            width: 640
        });
        const instance = dialogRef.content.instance as ImportMarketsDialogComponent;
        instance.mode = 'ranges';

        dialogRef.result.subscribe((result: any) => {
            if (result instanceof DialogCloseResult || !result.confirmed) {
                return;
            }
            this.applyImport(result.groups as MarketGroup[]);
        });
    }

    // Full replace: every imported market/block is treated as new from this session's
    // perspective (fresh id: null), matching how the real save already re-creates every row
    // with a fresh DB id regardless of what id it had before. Discards whatever was staged
    // pre-import - the popup's own warning says as much.
    private applyImport(groups: MarketGroup[]): void {
        this.markets = groups.map(group => this.toEditableMarket(group));
        this.markets.forEach(market => {
            market.isNew = true;
            market.blocks.forEach(block => {
                block.id = null;
                block.covered = undefined;
            });
        });
        // Each imported block carries its real rank (see parseRangesCsv), so this rebuilds the
        // flat cross-market priority order exactly the way applyResponse does after a GET.
        this.rebuildPriorityOrder();

        // 'external_edit' - a bulk change from outside the normal one-block-at-a-time flow,
        // not decomposed into per-block block_add entries (see market_config_audit's action
        // CHECK constraint, alembic revision 135).
        this.pendingChanges = this.markets.map(market => ({
            action: 'external_edit',
            market: market.name,
            before: null,
            after: {name: market.name, blocks: market.blocks.map(block => this.blockSnapshot(block))}
        }));

        this.expandedMarkets = new Set(this.markets.map(market => market.name));
        this.editingKey = null;
        this.draft = null;
        this.draftError = null;
        this.marketCount = this.markets.length;
        this.serviceCount = this.markets.reduce((total, market) => total + market.blocks.length, 0);
        this.rebuildRows();
        this.cdr.detectChanges();
        this.notifySuccess(`Imported ${this.markets.length} market${this.markets.length === 1 ? '' : 's'}. Not yet saved.`);
    }

    // Current on-screen state, unsaved edits included - deliberately not what the server has,
    // this is a pure client-side operation with no API round-trip. Built from priorityOrder
    // directly (already the correct flat, cross-market order), matching toRangesCsv's own
    // flat-list signature - a market-grouped structure can't represent an interleaved order.
    public exportMarkets(): void {
        const marketByBlock = this.marketNameByBlock();
        const rows = this.priorityOrder
            .filter(block => block.rsidFrom && block.rsidTo)
            .map(block => ({
                market: marketByBlock.get(block),
                block: {
                    id: block.id,
                    service: this.formatService(block),
                    days: block.days,
                    dateFrom: block.dateFrom,
                    dateTo: block.dateTo,
                    covered: block.covered ?? 0,
                    trainOrigin: block.trainOrigin,
                    trainDestination: block.trainDestination,
                    rank: block.rank ?? 0
                }
            }));
        this.downloadCsv(toRangesCsv(rows), 'market_rules.csv');
    }

    private marketNameByBlock(): Map<EditableBlock, string> {
        const marketByBlock = new Map<EditableBlock, string>();
        this.markets.forEach(market => market.blocks.forEach(block => marketByBlock.set(block, market.name)));
        return marketByBlock;
    }

    // ---- Priority order --------------------------------------------------------------------

    public openPriorityOrder(): void {
        const marketByBlock = this.marketNameByBlock();
        const overlapCounts = this.computeOverlapCounts();

        const dialogRef = this.dialogService.open({
            title: 'Priority order',
            content: PriorityOrderDialogComponent,
            appendTo: this.viewContainerRef,
            width: 720
        });
        const instance = dialogRef.content.instance as PriorityOrderDialogComponent;
        instance.rows = this.priorityOrder
            .filter(block => block.rsidFrom && block.rsidTo)
            .map(block => ({
                marketName: marketByBlock.get(block),
                service: this.formatService(block),
                days: block.days,
                dateFrom: block.dateFrom,
                dateTo: block.dateTo,
                overlapCount: overlapCounts.get(block.key) ?? 0,
                ref: block
            }));

        dialogRef.result.subscribe((result: any) => {
            if (result instanceof DialogCloseResult || !result.confirmed) {
                return;
            }
            this.applyPriorityOrder(result.rows as PriorityOrderRow[]);
        });
    }

    // Replaces priorityOrder with the dialog's (possibly reordered) rows, then logs one
    // priority_reorder pendingChange per market whose blocks' relative order actually changed -
    // not every market, and not decomposed into per-block entries (same convention as
    // external_edit). Any block the dialog never saw (e.g. mid-add, no service set yet) keeps
    // its prior relative position, appended after the reordered ones - the dialog only ever
    // reorders real rules, never drops or adds one.
    private applyPriorityOrder(rows: PriorityOrderRow[]): void {
        const before = this.orderedServicesByMarket(this.priorityOrder);

        const reordered: EditableBlock[] = rows.map(row => row.ref);
        const reorderedSet = new Set(reordered);
        const leftover = this.priorityOrder.filter(block => !reorderedSet.has(block));
        this.priorityOrder = [...reordered, ...leftover];

        const after = this.orderedServicesByMarket(this.priorityOrder);

        for (const market of this.markets) {
            const beforeServices = before.get(market.name) ?? [];
            const afterServices = after.get(market.name) ?? [];
            if (JSON.stringify(beforeServices) !== JSON.stringify(afterServices)) {
                this.pendingChanges.push({
                    action: 'priority_reorder', market: market.name,
                    before: {order: beforeServices}, after: {order: afterServices}
                });
            }
        }

        this.rebuildRows();
        this.cdr.detectChanges();
    }

    private orderedServicesByMarket(order: EditableBlock[]): Map<string, string[]> {
        const marketByBlock = this.marketNameByBlock();
        const result = new Map<string, string[]>();
        for (const block of order) {
            const marketName = marketByBlock.get(block);
            if (!marketName) {
                continue;
            }
            if (!result.has(marketName)) {
                result.set(marketName, []);
            }
            result.get(marketName).push(this.formatService(block));
        }
        return result;
    }

    // ---- Save ------------------------------------------------------------------------------

    public save(): void {
        this.openSaveDialog('ranges').subscribe(result => {
            if (!result) {
                return;
            }
            this.commitSave(result.reason);
        });
    }

    private commitSave(reason: string | null): void {
        // The market-grouped payload can't represent an interleaved cross-market order via
        // array position alone, so each block's true global rank travels explicitly, read
        // from priorityOrder's own position (the source of truth), not re-derived here.
        const rankByBlock = new Map<EditableBlock, number>(this.priorityOrder.map((block, index) => [block, index + 1]));

        const payload: MarketsSavePayload = {
            mode: 'ranges',
            markets: this.markets.map(market => ({
                name: market.name,
                blocks: market.blocks
                    .filter(block => block.rsidFrom && block.rsidTo)
                    .map(block => ({
                        id: block.id,
                        service: this.formatService(block),
                        days: block.days,
                        dateFrom: block.dateFrom,
                        dateTo: block.dateTo,
                        rank: rankByBlock.get(block),
                        trainOrigin: block.trainOrigin,
                        trainDestination: block.trainDestination
                    }))
            })),
            changes: this.pendingChanges.map(({itemKey, ...change}) => change),
            reason
        };

        this.saving = true;
        this.configurationSvc.saveMarketsConfig(payload).subscribe(response => {
            this.applyResponse(response);
            this.saving = false;
            this.notifySuccess('Market configuration saved.');
            this.cdr.detectChanges();
        }, error => {
            console.error(`Error saving markets config ${error}`);
            this.saving = false;
            // The API returns {"message": "..."} on a validation failure (e.g. an overlap) -
            // surface that instead of a generic failure when it's there.
            this.notifyError(error?.error?.message || 'Failed to save market configuration.');
            this.cdr.detectChanges();
        });
    }

    // ---- Row building ------------------------------------------------------------------------

    private rebuildRows(): void {
        const overlapCounts = this.computeOverlapCounts();
        const rows: MarketRow[] = [];
        for (const market of this.markets) {
            rows.push({
                isGroupRow: true,
                market: market.name,
                marketRef: market,
                itemCount: market.blocks.length,
                coveredSum: market.blocks.reduce((total, block) => total + (block.covered ?? 0), 0)
            });

            if (!this.expandedMarkets.has(market.name)) {
                continue;
            }

            for (const block of market.blocks) {
                if (block.key === this.editingKey) {
                    rows.push({isGroupRow: false, isEditRow: true, market: market.name, marketRef: market, blockRef: block});
                } else {
                    rows.push({
                        isGroupRow: false, market: market.name, marketRef: market, blockRef: block,
                        service: this.formatService(block), days: block.days, dateFrom: block.dateFrom,
                        dateTo: block.dateTo, covered: block.covered, overlapCount: overlapCounts.get(block.key) ?? 0
                    });
                }
            }
        }

        // Always last, never toggled: "Everything else -> Unassigned" is a standalone figure,
        // not a market with blocks of its own to expand or edit.
        if (this.unassigned) {
            rows.push({isGroupRow: false, isPinnedRow: true, market: this.unassigned.name, covered: this.unassigned.count});
        }

        this.gridData = {data: rows, total: rows.length};
    }
}
